import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
  Alert,
  TablePagination,
  Stack,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  Snackbar
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrganizationId } from '../utils/organizationContext';

const formatDate = (date) => {
  if (!date) return 'No date';
  return format(new Date(date), 'MMM d, yyyy');
};

const AssignFoundationSchool = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { organizationSlug: urlSlug } = useParams();
  const { user, organizationSlug } = useAuth();
  const [currentOrgId, setCurrentOrgId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load organization ID from user or URL slug
  useEffect(() => {
    const loadOrgId = async () => {
      // If user has organization_id, use it directly
      if (user?.organization_id) {
        setCurrentOrgId(user.organization_id);
        return;
      }
      
      // If user is super-admin and we have organization slug in URL, get org ID
      const slug = urlSlug || organizationSlug;
      if (slug && user?.role === 'system_admin' && !user?.organization_id) {
        try {
          const orgId = await getOrganizationId(slug);
          if (orgId) {
            setCurrentOrgId(orgId);
          }
        } catch (err) {
          console.error('Error fetching organization ID:', err);
        }
      }
    };
    loadOrgId();
  }, [urlSlug, organizationSlug, user]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });
      
      if (currentOrgId && (user?.role === 'system_admin' || user?.organization_id === currentOrgId)) {
        params.append('organization_id', currentOrgId.toString());
      }

      // Fetch foundation school students for assignment
      const studentsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/foundation-school-assignments/students?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setStudents(studentsResponse.data.data || []);
      setTotalCount(studentsResponse.data.total || 0);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      const errorMessage = err.response?.data?.error || 'Failed to load data. Please try again later.';
      setError(errorMessage);
      setStudents([]);
      setLoading(false);
    }
  }, [page, rowsPerPage, user, currentOrgId]);

  useEffect(() => {
    if (currentOrgId || user?.organization_id) {
      fetchData();
    }
  }, [fetchData, currentOrgId, user?.organization_id]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAssign = async (studentId, assignedClass) => {
    if (!assignedClass) return;
    
    try {
      setError('');
      setSuccess('');
      
      await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/foundation-school-assignments/assign`,
        {
          foundation_school_id: studentId,
          assigned_class: assignedClass
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      // Update the UI to reflect the assignment
      setStudents(prevStudents => prevStudents.map(student => {
        if (student.id === studentId) {
          return {
            ...student,
            assigned_class: assignedClass
          };
        }
        return student;
      }));
      
      setSnackbar({
        open: true,
        message: 'Student assigned to class successfully',
        severity: 'success'
      });
      
      // Refresh data to get updated assignments
      fetchData();
    } catch (err) {
      console.error('Error assigning student:', err);
      const errorMessage = err.response?.data?.error || 'Failed to assign student';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const isUnassigned = (student) => {
    return !student.assigned_class;
  };

  const filteredStudents = students;

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 2, my: 3 }}>
        <Typography variant="h4" gutterBottom>
          Assign Foundation School Classes
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>

            {isMobile ? (
              <Stack spacing={2}>
                {filteredStudents.length === 0 ? (
                  <Alert severity="info">No students found</Alert>
                ) : (
                  filteredStudents.map((student) => (
                    <Card key={student.id} sx={isUnassigned(student) ? { backgroundColor: 'rgba(255, 152, 0, 0.05)' } : {}}>
                      <CardContent>
                        <Stack spacing={1}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1" fontWeight="bold">
                              {student.first_name} {student.last_name}
                            </Typography>
                            {isUnassigned(student) ? (
                              <Chip label="Unassigned" color="warning" size="small" />
                            ) : (
                              <Chip label="Assigned" color="success" size="small" />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Enrollment Date: {formatDate(student.enrollment_date)}
                          </Typography>
                          <Box>
                            <Typography variant="body2">{student.phone_number || 'No phone'}</Typography>
                            <Typography variant="body2">{student.email || 'No email'}</Typography>
                          </Box>
                          {student.reason_for_enrollment && (
                            <Typography variant="body2" color="text.secondary">
                              {student.reason_for_enrollment.length > 50 
                                ? `${student.reason_for_enrollment.substring(0, 50)}...` 
                                : student.reason_for_enrollment}
                            </Typography>
                          )}
                          {!isUnassigned(student) && (
                            <Typography variant="body2" color="text.secondary">
                              Assigned to: {student.assigned_class === 'class_1' ? 'Class 1' : student.assigned_class === 'class_2' ? 'Class 2' : student.assigned_class}
                            </Typography>
                          )}
                          <FormControl fullWidth size="small">
                            <Select
                              value={student.assigned_class || ""}
                              onChange={(e) => handleAssign(student.id, e.target.value)}
                            >
                              <MenuItem value="">Select class...</MenuItem>
                              <MenuItem value="class_1">Class 1</MenuItem>
                              <MenuItem value="class_2">Class 2</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => {
                              const slug = urlSlug || organizationSlug;
                              navigate(slug ? `/${slug}/foundation-school/${student.id}` : `/foundation-school/${student.id}`);
                            }}
                          >
                            View Details
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Stack>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Enrollment Date</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Additional Info</TableCell>
                      <TableCell>Currently Assigned To</TableCell>
                      <TableCell>Assign To</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStudents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No students found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredStudents.map((student) => (
                        <TableRow 
                          key={student.id}
                          sx={isUnassigned(student) ? { backgroundColor: 'rgba(255, 152, 0, 0.05)' } : {}}
                        >
                          <TableCell>
                            {isUnassigned(student) ? (
                              <Chip label="Unassigned" color="warning" size="small" />
                            ) : (
                              <Chip label="Assigned" color="success" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {formatDate(student.enrollment_date)}
                          </TableCell>
                          <TableCell>
                            {student.first_name} {student.last_name}
                          </TableCell>
                          <TableCell>
                            <Box>
                              {student.phone_number || 'No phone'}
                              <br />
                              {student.email || 'No email'}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              {student.reason_for_enrollment && (
                                <Typography variant="body2" color="text.secondary">
                                  {student.reason_for_enrollment.length > 50 
                                    ? `${student.reason_for_enrollment.substring(0, 50)}...` 
                                    : student.reason_for_enrollment}
                                </Typography>
                              )}
                              {student.baptized_in_holy_ghost && (
                                <Chip 
                                  label={`Baptized: ${student.baptized_in_holy_ghost}`}
                                  color="info" 
                                  size="small" 
                                  sx={{ mt: 0.5 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {!isUnassigned(student) && (
                              <Typography variant="body2">
                                {student.assigned_class === 'class_1' ? 'Class 1' : student.assigned_class === 'class_2' ? 'Class 2' : student.assigned_class || 'N/A'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={student.assigned_class || ""}
                                onChange={(e) => handleAssign(student.id, e.target.value)}
                              >
                                <MenuItem value="">Select class...</MenuItem>
                                <MenuItem value="class_1">Class 1</MenuItem>
                                <MenuItem value="class_2">Class 2</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => {
                                const slug = urlSlug || organizationSlug;
                                navigate(slug ? `/${slug}/foundation-school/${student.id}` : `/foundation-school/${student.id}`);
                              }}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                sx={{
                  '& .MuiTablePagination-selectIcon': {
                    color: '#DC2626',
                  },
                  '& .MuiTablePagination-select': {
                    '&:focus': {
                      backgroundColor: '#fef2f2',
                    }
                  }
                }}
              />
            </Box>
          </>
        )}
        
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default AssignFoundationSchool;


